/**
 * Date Range Utilities
 * Pure functions for date range calculations
 */

import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  format,
} from '@ufc/core'

import type { DatePreset, DateRange } from '../types'

const DATE_FORMAT = 'yyyy-MM-dd'

/**
 * Get date range from a preset
 */
export function getDateRangeFromPreset(preset: DatePreset): { from: string; to: string } {
  const now = new Date()

  switch (preset) {
    case 'today':
      return {
        from: format(startOfDay(now), DATE_FORMAT),
        to: format(endOfDay(now), DATE_FORMAT),
      }

    case 'this_week':
      return {
        from: format(startOfWeek(now, { weekStartsOn: 0 }), DATE_FORMAT),
        to: format(endOfWeek(now, { weekStartsOn: 0 }), DATE_FORMAT),
      }

    case 'this_month':
      return {
        from: format(startOfMonth(now), DATE_FORMAT),
        to: format(endOfMonth(now), DATE_FORMAT),
      }

    case 'this_quarter':
      return {
        from: format(startOfQuarter(now), DATE_FORMAT),
        to: format(endOfQuarter(now), DATE_FORMAT),
      }

    case 'this_year':
      return {
        from: format(startOfYear(now), DATE_FORMAT),
        to: format(endOfYear(now), DATE_FORMAT),
      }

    case 'last_30_days':
      return {
        from: format(subDays(now, 30), DATE_FORMAT),
        to: format(now, DATE_FORMAT),
      }

    case 'custom':
    default:
      // Return current month as fallback
      return {
        from: format(startOfMonth(now), DATE_FORMAT),
        to: format(endOfMonth(now), DATE_FORMAT),
      }
  }
}

/**
 * Resolve date range - compute actual dates from preset if needed
 */
export function resolveDateRange(dateRange: DateRange): { from: string; to: string } {
  if (dateRange.preset === 'custom' && dateRange.from && dateRange.to) {
    return { from: dateRange.from, to: dateRange.to }
  }
  return getDateRangeFromPreset(dateRange.preset)
}

/**
 * Format date range for display
 */
export function formatDateRangeDisplay(dateRange: DateRange, locale: string = 'en'): string {
  const { from, to } = resolveDateRange(dateRange)

  const fromDate = new Date(from)
  const toDate = new Date(to)

  const formatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return `${formatter.format(fromDate)} - ${formatter.format(toDate)}`
}

/**
 * Check if two date ranges are equal
 */
export function areDateRangesEqual(a: DateRange, b: DateRange): boolean {
  if (a.preset !== b.preset) return false
  if (a.preset === 'custom') {
    return a.from === b.from && a.to === b.to
  }
  return true
}
